import React from 'react';

// Flux
import { useAppSelector } from '../../flux/configureStore';

// Constellation
import { Button, Divider, Heading, Modal, TextButton } from '@zillow/constellation-10';

// Misc / Utils
import { augmentError, formatForLog } from '../../utils/errors';
import { logger } from '../../index';

// Components
import ActiveRentals from '../ActiveRentals';
import Amenities from '../Amenities';
import Bedrooms from '../Bedrooms';
import Distance from '../Distance';
import OffMarket from '../OffMarket';
import PropertyType from '../PropertyType';

// Types
import type { PartialFilterType } from '../../models/Types';

const FilterModal = ({
  setIsLoading,
  allFiltersLabel,
}: {
  setIsLoading: (isLoading: boolean) => void;
  allFiltersLabel: string;
}) => {
  const setFilter = useAppSelector((state) => state.setFilter);
  const onFilterChange = async (partialFilterObj?: PartialFilterType) => {
    setIsLoading(true);
    try {
      await setFilter(partialFilterObj);
    } catch (err: any) {
      augmentError(err, {
        root: 'modules/FilterModal#onFilterChange',
      });

      logger.error(formatForLog(err), 'Filter: Error handling filter change in mobile modal');
    }
    setIsLoading(false);
  };
  const handleResetFilters = async () => {
    setIsLoading(true);
    try {
      await setFilter();
    } catch (err) {
      augmentError(err, {
        root: 'filters/FilterModal#handleResetFilters',
      });

      logger.error(formatForLog(err), 'Filter: Error handling filter reset in mobile modal');
    }
    setIsLoading(false);
  };
  return (
    <Modal.Root size={{ base: 'fullScreen', lg: 'md' }}>
      <Modal.Trigger>
        <Button tone="brand" emphasis="outlined" css={{ width: '220px' }}>
          {allFiltersLabel}
        </Button>
      </Modal.Trigger>
      <Modal.Portal>
        <Modal.Backdrop lockScroll>
          <Modal.Content>
            <Modal.CloseButton />
            <Modal.Header>
              <TextButton onClick={handleResetFilters} css={{ position: 'absolute', top: '0', height: '1400' }}>
                Reset all
              </TextButton>
              <Heading level={1} textStyle="body-lg-bold" css={{ textAlign: 'center' }}>
                All filters
              </Heading>
            </Modal.Header>
            <Divider />
            <Modal.Body css={{ '& > :last-child': { borderBottom: 'none' } }}>
              <ActiveRentals onFilterChange={onFilterChange} />
              <OffMarket onFilterChange={onFilterChange} />
              <Bedrooms onFilterChange={onFilterChange} />
              <PropertyType onFilterChange={onFilterChange} />
              <Distance onFilterChange={onFilterChange} />
              <Amenities onFilterChange={onFilterChange} />
            </Modal.Body>
            <Divider />
            <Modal.Footer css={{ backgroundColor: 'bg.accent.brand.hero' }}>
              <Modal.Close>
                <Button tone="brand" emphasis="outlined">
                  Done
                </Button>
              </Modal.Close>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

export default FilterModal;
